// SEO Landing Page Data for Job Role Templates
// This file contains all content for SEO landing pages

export const SEO_TEMPLATES = {
  "devops-engineer": {
    slug: "devops-engineer",
    title: "DevOps Engineer Resume Template",
    metaTitle: "DevOps Engineer Resume Template – ATS Optimized | FixResume24",
    metaDescription: "Create a professional DevOps Engineer resume with our ATS-optimized template. Includes CI/CD, Kubernetes, Docker skills. Start building for free!",
    heroTitle: "DevOps Engineer",
    heroSubtitle: "Build a powerful resume that showcases your infrastructure and automation expertise",
    templateId: "engineer_pro", // Maps to existing template
    jobDescription: "DevOps Engineers bridge the gap between development and operations, implementing CI/CD pipelines, managing cloud infrastructure, and ensuring system reliability. They are essential for organizations adopting agile methodologies and cloud-native architectures.",
    averageSalary: "$95,000 - $150,000",
    demandLevel: "Very High",
    keySkills: [
      "CI/CD (Jenkins, GitLab CI, GitHub Actions)",
      "Container Orchestration (Kubernetes, Docker Swarm)",
      "Infrastructure as Code (Terraform, Ansible, CloudFormation)",
      "Cloud Platforms (AWS, Azure, GCP)",
      "Monitoring & Logging (Prometheus, Grafana, ELK Stack)",
      "Scripting (Python, Bash, PowerShell)",
      "Version Control (Git, GitLab, Bitbucket)",
      "Linux Administration",
      "Networking & Security",
      "Agile/Scrum Methodologies"
    ],
    atsKeywords: [
      "DevOps", "CI/CD", "Kubernetes", "Docker", "AWS", "Azure", "GCP",
      "Terraform", "Ansible", "Jenkins", "GitLab", "Infrastructure as Code",
      "Microservices", "Containerization", "Cloud Architecture", "SRE",
      "Site Reliability", "Automation", "Linux", "Monitoring"
    ],
    summaryExamples: [
      "Results-driven DevOps Engineer with 5+ years of experience designing and implementing CI/CD pipelines, reducing deployment time by 70%. Expert in Kubernetes, Docker, and AWS with a proven track record of improving system reliability to 99.9% uptime.",
      "Certified AWS DevOps Engineer with expertise in infrastructure automation using Terraform and Ansible. Successfully migrated 50+ applications to microservices architecture, resulting in 40% cost reduction and improved scalability."
    ],
    recruiterTips: [
      "Quantify your achievements - mention deployment frequency improvements, uptime percentages, and cost savings",
      "List specific tools and technologies you've used in production environments",
      "Highlight any certifications (AWS, Azure, Kubernetes CKA/CKAD)",
      "Include experience with both legacy systems and modern cloud-native architectures",
      "Mention team collaboration and cross-functional experience"
    ],
    relatedRoles: ["aws-engineer", "software-engineer", "data-analyst"]
  },

  "software-engineer": {
    slug: "software-engineer",
    title: "Software Engineer Resume Template",
    metaTitle: "Software Engineer Resume Template – ATS Optimized | FixResume24",
    metaDescription: "Create a standout Software Engineer resume with our ATS-friendly template. Highlight your coding skills, projects, and experience. Free to start!",
    heroTitle: "Software Engineer",
    heroSubtitle: "Showcase your coding expertise and build a resume that gets noticed by top tech companies",
    templateId: "engineer_pro",
    jobDescription: "Software Engineers design, develop, and maintain software applications. They work across the full development lifecycle, from requirements gathering to deployment, using various programming languages and frameworks to solve complex problems.",
    averageSalary: "$80,000 - $160,000",
    demandLevel: "Very High",
    keySkills: [
      "Programming Languages (Python, Java, JavaScript, C++)",
      "Web Frameworks (React, Angular, Node.js, Django)",
      "Database Management (SQL, PostgreSQL, MongoDB)",
      "Version Control (Git, GitHub)",
      "RESTful APIs & Microservices",
      "Data Structures & Algorithms",
      "Unit Testing & TDD",
      "Agile Development",
      "Cloud Services (AWS, GCP, Azure)",
      "Problem Solving & Debugging"
    ],
    atsKeywords: [
      "Software Development", "Full Stack", "Backend", "Frontend", "API",
      "Python", "Java", "JavaScript", "React", "Node.js", "SQL",
      "Agile", "Scrum", "Git", "REST", "Microservices", "OOP",
      "Data Structures", "Algorithms", "Testing"
    ],
    summaryExamples: [
      "Full-stack Software Engineer with 4+ years of experience building scalable web applications using React, Node.js, and PostgreSQL. Led development of e-commerce platform serving 100K+ daily users with 99.5% uptime.",
      "Detail-oriented Software Engineer specializing in backend development with Python and Django. Optimized database queries resulting in 60% performance improvement. Strong advocate for clean code and test-driven development."
    ],
    recruiterTips: [
      "Include links to your GitHub profile and notable projects",
      "Mention specific technologies used in each role with versions if relevant",
      "Quantify impact - users served, performance improvements, features shipped",
      "Highlight any open-source contributions",
      "Show progression in responsibilities and technical complexity"
    ],
    relatedRoles: ["devops-engineer", "data-analyst", "fresher"]
  },

  "aws-engineer": {
    slug: "aws-engineer",
    title: "AWS Engineer Resume Template",
    metaTitle: "AWS Cloud Engineer Resume Template – ATS Optimized | FixResume24",
    metaDescription: "Build an impressive AWS Cloud Engineer resume with our ATS-optimized template. Showcase your cloud architecture skills. Start free today!",
    heroTitle: "AWS Cloud Engineer",
    heroSubtitle: "Demonstrate your cloud expertise with a resume built for the AWS ecosystem",
    templateId: "tech_dark",
    jobDescription: "AWS Engineers design, implement, and manage cloud infrastructure on Amazon Web Services. They optimize cloud resources for cost and performance while ensuring security and compliance across cloud environments.",
    averageSalary: "$100,000 - $165,000",
    demandLevel: "Very High",
    keySkills: [
      "AWS Services (EC2, S3, Lambda, RDS, EKS)",
      "Infrastructure as Code (CloudFormation, Terraform)",
      "AWS Security (IAM, VPC, Security Groups)",
      "Containerization (ECS, EKS, Fargate)",
      "Serverless Architecture (Lambda, API Gateway)",
      "Database Services (RDS, DynamoDB, ElastiCache)",
      "Monitoring (CloudWatch, X-Ray)",
      "Cost Optimization",
      "Migration Strategies",
      "High Availability & Disaster Recovery"
    ],
    atsKeywords: [
      "AWS", "Amazon Web Services", "EC2", "S3", "Lambda", "CloudFormation",
      "IAM", "VPC", "EKS", "ECS", "RDS", "DynamoDB", "CloudWatch",
      "Serverless", "Infrastructure as Code", "Cloud Architecture",
      "DevOps", "Terraform", "High Availability"
    ],
    summaryExamples: [
      "AWS Certified Solutions Architect with 6+ years of experience designing and implementing scalable cloud solutions. Reduced infrastructure costs by 45% through right-sizing and Reserved Instance optimization while maintaining 99.99% availability.",
      "Senior AWS Engineer specializing in serverless architectures and container orchestration. Successfully migrated enterprise applications to AWS, serving 1M+ requests daily with sub-100ms latency."
    ],
    recruiterTips: [
      "List all AWS certifications prominently (Solutions Architect, Developer, SysOps)",
      "Include specific AWS services you've worked with in production",
      "Quantify cost savings and performance improvements",
      "Mention experience with AWS Well-Architected Framework",
      "Highlight multi-account and enterprise-scale experience"
    ],
    relatedRoles: ["devops-engineer", "software-engineer", "data-analyst"]
  },

  "data-analyst": {
    slug: "data-analyst",
    title: "Data Analyst Resume Template",
    metaTitle: "Data Analyst Resume Template – ATS Optimized | FixResume24",
    metaDescription: "Create a data-driven resume with our ATS-optimized Data Analyst template. Highlight your analytics skills and insights. Start building free!",
    heroTitle: "Data Analyst",
    heroSubtitle: "Transform your analytical skills into a compelling resume that drives interviews",
    templateId: "data_science",
    jobDescription: "Data Analysts collect, process, and analyze data to help organizations make informed decisions. They create visualizations, build reports, and identify trends that drive business strategy and operational improvements.",
    averageSalary: "$60,000 - $110,000",
    demandLevel: "High",
    keySkills: [
      "SQL & Database Querying",
      "Python/R for Data Analysis",
      "Data Visualization (Tableau, Power BI, Looker)",
      "Excel & Advanced Spreadsheets",
      "Statistical Analysis",
      "ETL Processes",
      "A/B Testing",
      "Business Intelligence Tools",
      "Data Cleaning & Preparation",
      "Reporting & Dashboards"
    ],
    atsKeywords: [
      "Data Analysis", "SQL", "Python", "Tableau", "Power BI", "Excel",
      "Statistics", "Data Visualization", "ETL", "Business Intelligence",
      "Reporting", "Dashboard", "Analytics", "KPI", "Metrics",
      "Data Mining", "A/B Testing", "Insights"
    ],
    summaryExamples: [
      "Data Analyst with 3+ years of experience transforming complex datasets into actionable insights. Built executive dashboards in Tableau that reduced reporting time by 80% and identified $2M in cost-saving opportunities.",
      "Results-oriented Data Analyst skilled in SQL, Python, and Power BI. Developed predictive models that improved customer retention by 25% and automated weekly reporting processes saving 15+ hours per week."
    ],
    recruiterTips: [
      "Showcase specific business impact of your analyses",
      "Include examples of dashboards or reports you've created",
      "Mention the size of datasets you've worked with",
      "Highlight cross-functional collaboration with stakeholders",
      "List relevant certifications (Google Analytics, Tableau, etc.)"
    ],
    relatedRoles: ["software-engineer", "aws-engineer", "fresher"]
  },

  "fresher": {
    slug: "fresher",
    title: "Fresher Resume Template",
    metaTitle: "Fresher Resume Template for Entry-Level Jobs – ATS Optimized | FixResume24",
    metaDescription: "Perfect resume template for freshers and recent graduates. ATS-optimized to land your first job. Highlight projects, skills, and education. Free!",
    heroTitle: "Fresher / Entry-Level",
    heroSubtitle: "Launch your career with a professional resume that highlights your potential",
    templateId: "clean_lines",
    jobDescription: "As a fresher or recent graduate, you bring fresh perspectives, up-to-date knowledge, and enthusiasm to potential employers. Your resume should highlight academic achievements, projects, internships, and transferable skills.",
    averageSalary: "$45,000 - $70,000",
    demandLevel: "High",
    keySkills: [
      "Academic Projects & Coursework",
      "Programming Fundamentals",
      "Communication Skills",
      "Problem Solving",
      "Team Collaboration",
      "Time Management",
      "Adaptability & Learning",
      "Basic Technical Skills",
      "Internship Experience",
      "Extracurricular Activities"
    ],
    atsKeywords: [
      "Entry Level", "Fresh Graduate", "Bachelor's Degree", "Internship",
      "Academic Projects", "GPA", "Coursework", "Team Player",
      "Quick Learner", "Communication", "Problem Solving",
      "Technical Skills", "Eager to Learn", "Recent Graduate"
    ],
    summaryExamples: [
      "Motivated Computer Science graduate with strong foundation in programming (Python, Java) and hands-on project experience. Completed internship at tech startup where I developed features used by 10K+ users. Eager to contribute to innovative team.",
      "Recent Business Administration graduate with internship experience in data analysis. Proficient in Excel, SQL, and Tableau. Led university marketing club increasing membership by 40%. Seeking entry-level analyst position to apply analytical skills."
    ],
    recruiterTips: [
      "Lead with education if you have limited work experience",
      "Include relevant coursework and academic projects with outcomes",
      "Highlight internships, part-time jobs, and volunteer work",
      "Showcase leadership in clubs, sports, or organizations",
      "Mention relevant certifications or online courses completed",
      "Keep it to one page - quality over quantity"
    ],
    relatedRoles: ["software-engineer", "data-analyst", "devops-engineer"]
  }
};

// Get all template slugs for sitemap generation
export const getAllTemplateSlugs = () => {
  return Object.keys(SEO_TEMPLATES);
};

// Get template by slug
export const getTemplateBySlug = (slug) => {
  return SEO_TEMPLATES[slug] || null;
};
